import React from 'react';
import { Crown, AlertTriangle, ShieldAlert } from 'lucide-react';
import { SupabaseService } from '../services/supabaseService';

interface AbdicationModalProps {
    isOpen: boolean;
    onClose: () => void;
    reason: 'bankruptcy' | 'revoked' | 'ineligible';
}

const AbdicationModal: React.FC<AbdicationModalProps> = ({ isOpen, onClose, reason }) => {
    if (!isOpen) return null;

    const handleAbdicate = async () => {
        // If bankrupt, we confirm abdication to reset state (gold=0, eligible=false)
        await SupabaseService.abdicateRanked();
        onClose();
        // Maybe trigger a refresh or navigation?
        window.location.reload();
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center z-[100]">
            <div className="absolute inset-0 bg-black/95 backdrop-blur-md" onClick={onClose}></div>

            <div className="relative bg-tudor-black border-2 border-red-900/50 p-8 max-w-md w-full shadow-[0_0_100px_rgba(127,29,29,0.3)] animate-fade-in-up text-center">
                <div className="flex justify-center mb-6">
                    <div className="relative">
                        <Crown className="w-20 h-20 text-zinc-800" />
                        <ShieldAlert className="w-10 h-10 text-red-600 absolute bottom-0 right-0" />
                    </div>
                </div>

                <h2 className="text-3xl text-red-600 font-serif mb-2 uppercase tracking-widest">Abdication</h2>

                <div className="h-px w-24 bg-red-900/50 mx-auto my-6"></div>

                <p className="text-gray-300 font-serif text-lg leading-relaxed mb-6">
                    {reason === 'bankruptcy' && (
                        <>
                            Your treasury is empty. The High Council has withdrawn its support.<br /><br />
                            <span className="text-red-500 font-bold">You must Abdicate your Rank.</span>
                        </>
                    )}
                    {reason === 'ineligible' && (
                        <>
                            You have not yet proven yourself worthy of the Crown's banner.<br /><br />
                            Complete a <span className="text-tudor-gold">Standard Campaign</span> to demonstrate your competence.
                        </>
                    )}
                    {reason === 'revoked' && (
                        <>
                            Your right to Ranked Warfare has been suspended.<br /><br />
                            You must rebuild your reputation in the Standard Campaigns.
                        </>
                    )}
                </p>

                <div className="bg-red-950/20 border border-red-900/30 p-4 rounded mb-8 text-sm text-red-400">
                    <p className="uppercase tracking-widest font-bold text-xs mb-2">The Decree</p>
                    Ranked access is revoked. You must complete a Standard Campaign to reclaim your status and Treasury.
                </div>

                <button
                    onClick={reason === 'bankruptcy' ? handleAbdicate : onClose}
                    className="w-full bg-transparent border border-red-800 text-red-600 hover:bg-red-950/50 hover:text-red-500 hover:border-red-500 py-3 px-6 rounded uppercase tracking-[0.2em] transition-all duration-300"
                >
                    {reason === 'bankruptcy' ? "I Abdicate" : "Return to Court"}
                </button>
            </div>
        </div>
    );
};

export default AbdicationModal;
